/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { MenuItem, Grid } from '@mui/material';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import type { Role } from '@opengeoweb/api';
import { GEOWEB_ROLE_PRESETS_ADMIN } from '@opengeoweb/api';
import {
  useAuthenticationContext,
  UserMenuRoles,
} from '@opengeoweb/authentication';
import { LanguageSelect, saveLanguageToLocalStorage } from '@opengeoweb/core';
import { UserMenuItemSentryRecording } from '@opengeoweb/sentry';
import {
  UserMenu as SharedUserMenu,
  useUserMenu,
  getInitials,
  UserMenuItemTheme,
  UserMenuItemCheatSheet,
} from '@opengeoweb/shared';
import type { ConfigType } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { ProjectDocInfo } from './ProjectDocInfo';
import ReleaseNotes from './ReleaseNotes';
import { useGeowebTranslation } from '../../i18n';
import { useBeVersion } from '../utils/app-api/hooks';

const styles = {
  username: {
    fontWeight: 500,
  },
  versionmenu: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    display: 'block',
  },
};

interface UserProps {
  userName: string;
  userRoles?: Role[];
  isAuthConfigured?: boolean;
  config?: ConfigType;
  recordButtonEnabled?: boolean;
  setRecordButtonEnabled?: React.Dispatch<React.SetStateAction<boolean>>;
  sentryInitialized?: boolean;
}

const UserMenu: React.FC<UserProps> = ({
  userName,
  userRoles,
  isAuthConfigured = false,
  config,
  recordButtonEnabled,
  setRecordButtonEnabled,
  sentryInitialized,
}: UserProps) => {
  const dispatch = useDispatch();
  const { t } = useGeowebTranslation();
  const navigate = useNavigate();
  const { isOpen, onClose, onToggle } = useUserMenu();
  const { data, error, isFetching } = useBeVersion();
  const [showReleaseNotes, setShowReleaseNotes] = React.useState(false);
  const [showFeReleaseNotes, setShowFeReleaseNotes] = React.useState(false);
  const { currentRole, setCurrentRole } = useAuthenticationContext();
  const isAdmin = currentRole?.name === GEOWEB_ROLE_PRESETS_ADMIN.name;
  const getBeVersionMessage = (): string => {
    if (isFetching) {
      return t('geoweb-user-menu-loading');
    }
    if (error) {
      return t('geoweb-user-menu-loading-failed', {
        errorMessage: error.message,
      });
    }
    return t('geoweb-user-menu-version-has-been-released', {
      version: data?.version,
    });
  };

  const feVersion =
    process.env.NX_PUBLIC_GEOWEB_COMMIT ?? process.env.NX_PUBLIC_GEOWEB_VERSION;

  // For KNMI, set GW_FEATURE_MENU_VERSION to true to display the link pointing towards beReleaseNotesPageLink. To display technical notes, also add GW_TECHNICAL_RELEASE_NOTES_URL with the appropriate link
  // For FMI and MetNo, set GW_FEATURE_MENU_FE_VERSION to true and if appropriate the correct link via GW_VERSION_PROGRESS_NOTES_URL (otherwise it will point to gitlab). To display technical notes, also add GW_TECHNICAL_RELEASE_NOTES_URL with the appropriate link

  const beReleaseNotesPageLink = `${config?.GW_BE_RELEASE_URL}` || '';

  const feVersionProgressNotesLink =
    config?.GW_VERSION_PROGRESS_NOTES_URL ??
    'https://gitlab.com/groups/opengeoweb/-/wikis/Version-Progress-Notes';

  let feTechnicalReleaseNotesLink;

  if (config?.GW_TECHNICAL_RELEASE_NOTES_URL) {
    feTechnicalReleaseNotesLink = config.GW_TECHNICAL_RELEASE_NOTES_URL;
  } else if (process.env.NX_PUBLIC_GEOWEB_COMMIT) {
    feTechnicalReleaseNotesLink = `https://gitlab.com/opengeoweb/opengeoweb/-/commits/${process.env.NX_PUBLIC_GEOWEB_COMMIT}`;
  } else {
    feTechnicalReleaseNotesLink = `https://gitlab.com/opengeoweb/opengeoweb/-/releases/v${process.env.NX_PUBLIC_GEOWEB_VERSION}`;
  }

  const handleLogout = (): void => {
    onClose();
    void navigate('/logout');
  };

  const handleLogin = (): void => {
    onClose();
    void navigate('/login');
  };

  const openReleaseNotes = (): void => {
    onClose();
    setShowReleaseNotes(true);
  };

  const openFeReleaseNotes = (): void => {
    onClose();
    setShowFeReleaseNotes(true);
  };

  const onOpenFeedback = (url: string): void => {
    window.open(url, '_blank');
  };

  const handleChangeRole = (role: Role): void => {
    setCurrentRole?.(role);

    const message: snackbarTypes.TranslatableMessage = {
      type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
      key: 'geoweb-role-message-assigned',
      params: {
        role: t(role.title),
      },
    };
    dispatch(snackbarActions.openSnackbar(message));
  };

  const isGuestUser = userName === 'Guest';

  const loginLogoutObject = isGuestUser ? (
    <MenuItem data-testid="loginButton" onClick={handleLogin}>
      {t('geoweb-user-menu-login')}
    </MenuItem>
  ) : (
    <MenuItem data-testid="logoutButton" onClick={handleLogout}>
      {t('geoweb-user-menu-logout')}
    </MenuItem>
  );

  return (
    <>
      <SharedUserMenu
        initials={getInitials(userName)}
        isAdmin={isAdmin}
        isOpen={isOpen}
        onClose={onClose}
        onToggle={onToggle}
      >
        <MenuItem divider selected>
          <Grid container>
            <Grid sx={styles.username}>{userName}</Grid>
          </Grid>
        </MenuItem>

        {!isGuestUser && (
          <MenuItem divider>
            <UserMenuRoles
              currentRole={currentRole}
              onChangeRole={handleChangeRole}
              roles={userRoles}
            />
          </MenuItem>
        )}

        <MenuItem divider sx={{ padding: '11px 16px' }}>
          <LanguageSelect onChangeLanguage={saveLanguageToLocalStorage} />
        </MenuItem>

        <UserMenuItemTheme
          enableSpecialThemes={config?.GW_FEATURE_ENABLE_SPECIAL_THEMES}
        />
        {config?.GW_FEATURE_MENU_VERSION ? (
          <MenuItem
            data-testid="be-version-menu"
            divider
            onClick={openReleaseNotes}
            sx={styles.versionmenu}
          >
            {getBeVersionMessage()}
          </MenuItem>
        ) : null}
        {config?.GW_FEATURE_MENU_FE_VERSION && feVersion ? (
          <MenuItem
            data-testid="fe-version-menu"
            divider
            onClick={openFeReleaseNotes}
            sx={styles.versionmenu}
          >
            {t('geoweb-user-menu-release-notes')}
            <br />
            {`v${feVersion}`}
          </MenuItem>
        ) : null}
        <UserMenuItemCheatSheet />
        {sentryInitialized ? (
          <UserMenuItemSentryRecording
            recordButtonEnabled={recordButtonEnabled}
            setRecordButtonEnabled={setRecordButtonEnabled}
          />
        ) : null}

        {config?.GW_FEATURE_MENU_FEEDBACK &&
        typeof config?.GW_FEATURE_MENU_FEEDBACK === 'string' ? (
          <MenuItem
            data-testid="open-feedback"
            divider
            onClick={() =>
              onOpenFeedback(config.GW_FEATURE_MENU_FEEDBACK as string)
            }
          >
            {t('geoweb-user-menu-feedback')}
          </MenuItem>
        ) : null}
        {config?.GW_FEATURE_MENU_INFO ? <ProjectDocInfo /> : null}
        {isAuthConfigured ? loginLogoutObject : null}
      </SharedUserMenu>
      {config?.GW_FEATURE_MENU_VERSION && showReleaseNotes ? (
        <ReleaseNotes
          config={config}
          handleClose={(): void => setShowReleaseNotes(false)}
          releasePage={feTechnicalReleaseNotesLink}
          version={getBeVersionMessage()}
          versionProgressNotesPage={beReleaseNotesPageLink}
        />
      ) : null}
      {config?.GW_FEATURE_MENU_FE_VERSION && showFeReleaseNotes ? (
        <ReleaseNotes
          config={config}
          handleClose={(): void => setShowFeReleaseNotes(false)}
          releasePage={feTechnicalReleaseNotesLink}
          version={feVersion!}
          versionProgressNotesPage={feVersionProgressNotesLink}
        />
      ) : null}
    </>
  );
};

export default UserMenu;
